import { Component, OnInit, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { SecretSourcesComponent } from '../../components/secret-sources';
import {
  FeaturesComponent,
  FeatureDisplayItem,
} from '../../components/features';
import { SecretsComponent } from '../../components/secrets';
import { ConnectionsComponent } from '../../components/connections';
import { PushDestinationsComponent } from '../../components/push-destinations';
import { StorageGrantsComponent } from '../../components/storage-grants';
import { PlansComponent } from '../../components/plans';
import { ProjectAccessComponent } from '../../components/project-access/project-access.component';
import { WasmModulesComponent } from '../../components/wasm-modules';
import { ProjectWasmModule } from '../../shared/interfaces/wasm-module.interface';
import { ExecPlan } from '../../shared/interfaces';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { ProjectService } from '../../shared/services/project/project.service';
import { PermissionId } from '../../shared/constants/permissions.constants';
import { FullProject } from '@app/shared/interfaces';

interface ProjectSettingsData {
  projectId: number;
  currentVersion?: any;
  currentUserId?: number;
}

@Component({
  selector: 'app-project-settings-page',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    SecretSourcesComponent,
    FeaturesComponent,
    SecretsComponent,
    ConnectionsComponent,
    PushDestinationsComponent,
    StorageGrantsComponent,
    PlansComponent,
    ProjectAccessComponent,
    WasmModulesComponent,
  ],
  templateUrl: './project-settings-page.component.html',
  styleUrls: ['./project-settings-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectSettingsPageComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ProjectSettingsPageComponent>>(MatDialogRef);
  data = inject<ProjectSettingsData>(MAT_DIALOG_DATA);
  appStateService = inject(AppStateService);
  /** Adding a connection is admin-only (EHQ-427). */
  readonly canManageConnections = this.appStateService.canManageConnections;
  projectService = inject(ProjectService);

  /** Tab indices in the settings dialog */
  private static readonly SECRET_SOURCES_TAB_INDEX = 1;
  private static readonly SECRETS_TAB_INDEX = 2;
  @ViewChild('tabGroup') tabGroup!: MatTabGroup;
  @ViewChild('secretsComponent') secretsComponent!: SecretsComponent;

  projectId: number | null = null;
  currentVersion: any = null;
  currentUserId: number = 0;
  projectData = signal<FullProject | null>(null);

  constructor() {
    this.projectId = this.data.projectId;
    this.currentVersion = this.data.currentVersion;
    this.currentUserId = this.data.currentUserId || 0;
  }

  ngOnInit(): void {
    this.loadProjectData();
  }

  private loadProjectData(): void {
    if (!this.projectId) return;

    this.projectService.getProject(this.projectId).subscribe({
      next: (project: FullProject) => {
        this.projectData.set(project);
      },
      error: (error) => {
        console.error('Error loading project data:', error);
      },
    });
  }

  /**
   * Check if the current user is the project owner
   */
  get isProjectOwner(): boolean {
    // First try AppStateService
    const appStateResult = this.appStateService.isProjectOwner();

    // If AppStateService doesn't have the project or result is false, try direct comparison
    let result = appStateResult;
    if (!result && this.projectData() && this.currentUserId) {
      result = this.projectData()!.userId === this.currentUserId;
    }

    return result;
  }

  /**
   * Check if the current user has admin permissions for the project
   */
  get hasAdminPermission(): boolean {
    // First try the standard permission check
    let result = this.projectService.getCurrentUserHasProjectPermission(
      PermissionId.ADMIN,
    );

    // If that fails and we have project data, try direct permission check
    if (!result && this.projectData() && this.appStateService.user()) {
      // Convert FullProject to Project type for compatibility
      const projectForPermissionCheck = this.convertFullProjectToProject(
        this.projectData()!,
      );
      result = this.projectService.getUserHasPermissionForProject(
        projectForPermissionCheck,
        this.appStateService.user()!,
        PermissionId.ADMIN,
      );
    }

    return result;
  }

  /**
   * Convert FullProject to Project type for compatibility with permission checks
   */
  private convertFullProjectToProject(fullProject: FullProject): any {
    return {
      ...fullProject,
      currentEditorId: fullProject.currentEditorId ?? null,
    };
  }

  /**
   * Check if the current user can create plans (owner or admin)
   */
  get canCreatePlans(): boolean {
    const isOwner = this.isProjectOwner;
    const hasAdmin = this.hasAdminPermission;
    const result = isOwner || hasAdmin;
    return result;
  }

  /**
   * Whether the current user may configure other members' access (EHQ-187).
   *
   * The grant CRUD and scope endpoints are project-`Admin`-gated, so a
   * non-admin gets the read-only view of their own access instead — asking for
   * the admin view would only produce a 403 they cannot act on.
   */
  get canAdministerAccess(): boolean {
    return this.isProjectOwner || this.hasAdminPermission;
  }

  // Event handlers for Plans
  onAddPlan(): void {
    console.log('Add new plan');
  }

  onEditPlan(plan: ExecPlan): void {
    console.log('Edit plan:', plan);
  }

  onDeletePlan(plan: ExecPlan): void {
    console.log('Delete plan:', plan);
  }

  // Event handlers for Features
  onFeatureToggled(feature: FeatureDisplayItem): void {
    console.log('Toggle feature:', feature);
  }

  /** Switch to Secret Sources tab when user needs to create one first */
  switchToSecretSourcesTab(): void {
    if (this.tabGroup) {
      this.tabGroup.selectedIndex =
        ProjectSettingsPageComponent.SECRET_SOURCES_TAB_INDEX;
    }
  }

  /** When switching to Secrets tab, refresh in case user just created a secret source */
  onTabChange(selectedIndex: number): void {
    if (
      selectedIndex === ProjectSettingsPageComponent.SECRETS_TAB_INDEX &&
      this.secretsComponent
    ) {
      this.secretsComponent.refreshData();
    }
  }

  // Connections — the shared ConnectionsComponent owns all add/edit/test/delete/
  // duplicate orchestration (via ConnectionsListStore + ConnectionFlowService).
  // The Settings tab only renders the Add button + count and delegates Add to it.
  @ViewChild(ConnectionsComponent) connectionsComponent?: ConnectionsComponent;
  connectionsCount = signal(0);

  /** Mirror the shared component's connection count for the tab header. */
  onConnectionsCountChange(count: number): void {
    this.connectionsCount.set(count);
  }

  /** Delegate to the shared component, which runs the add-connection flow. */
  addConnection(): void {
    this.connectionsComponent?.addConnection();
  }

  // Push Destinations — the shared PushDestinationsComponent owns all
  // add/edit/test/toggle/delete orchestration (via PushDestinationsListStore).
  // The Settings tab only renders the Add button + count and delegates Add to it,
  // mirroring the Connections tab.
  @ViewChild(PushDestinationsComponent)
  pushDestinationsComponent?: PushDestinationsComponent;
  pushDestinationsCount = signal(0);

  /** Mirror the shared component's destination count for the tab header. */
  onPushDestinationsCountChange(count: number): void {
    this.pushDestinationsCount.set(count);
  }

  /** Delegate to the shared component, which opens the add-destination dialog. */
  addPushDestination(): void {
    this.pushDestinationsComponent?.addDestination();
  }

  // Event handlers for WASM Modules
  onDeleteWasmModule(module: ProjectWasmModule): void {
    console.log('WASM module deleted:', module.name);
  }

  onClose(): void {
    // Close the dialog
    this.dialogRef.close();
  }
}
